import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { backupConfigs } from '@/api/backupConfigs';
import type {
  BackupConfigView, BackupConfigBody, BackupProvider, BackupVerifyResult,
} from '@/api/backupConfigs';
import type { ApiError } from '@/api/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Cloud, CheckCircle2, AlertCircle, Loader2, HardDriveDownload, Trash2, Lock,
} from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Reusable backup-config card — drops into customer detail + reseller
 * detail pages. Read/write tied to the tenant's own backup bucket
 * (R2 or S3). Credentials encrypted on the server; portal only shows
 * a redacted access_key after save.
 */
export function BackupConfigCard({
  scope, tenantId, title,
}: {
  scope: 'customer' | 'reseller';
  tenantId: string;
  title?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const queryKey = ['backup-config', scope, tenantId];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (scope === 'customer') {
        try {
          return await backupConfigs.getForCustomer(tenantId);
        } catch (err: unknown) {
          // If the feature flag is off, backend returns 403 feature_disabled.
          // Surface this as a non-error sentinel so we render the locked card.
          const e = err as ApiError;
          if (e?.status === 403 && e?.code === 'feature_disabled') {
            return { featureDisabled: true as const, config: null };
          }
          throw err;
        }
      }
      return backupConfigs.getForReseller(tenantId);
    },
  });

  const [editing, setEditing] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [provider, setProvider] = useState<BackupProvider>('r2');
  const [accountId, setAccountId] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [region, setRegion] = useState('auto');
  const [bucket, setBucket] = useState('');
  const [prefix, setPrefix] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<BackupVerifyResult | null>(null);

  // Hydrate form from the current row when opening the editor
  useEffect(() => {
    if (!editing || !data?.config) return;
    const c = data.config;
    setEnabled(c.enabled);
    setProvider(c.provider);
    setAccountId(c.account_id ?? '');
    setEndpoint(c.endpoint);
    setRegion(c.region);
    setBucket(c.bucket);
    setPrefix(c.prefix ?? '');
    setAccessKeyId(''); setSecretAccessKey('');
    setSaveError(null);
  }, [editing, data]);

  // Auto-derive R2 endpoint from account_id when user types one in
  useEffect(() => {
    if (provider === 'r2' && accountId && !endpoint.includes(accountId)) {
      setEndpoint(`https://${accountId}.r2.cloudflarestorage.com`);
    }
    if (provider === 'r2' && region !== 'auto') setRegion('auto');
  }, [provider, accountId, endpoint, region]);

  const save = useMutation({
    mutationFn: (body: BackupConfigBody) =>
      scope === 'customer'
        ? backupConfigs.putForCustomer(tenantId, body)
        : backupConfigs.putForReseller(tenantId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setEditing(false);
      setSaveError(null);
      setAccessKeyId(''); setSecretAccessKey('');
    },
    onError: (err: unknown) =>
      setSaveError(err instanceof Error ? err.message : t('common.failedToSave')),
  });

  const del = useMutation({
    mutationFn: () =>
      scope === 'customer'
        ? backupConfigs.removeForCustomer(tenantId)
        : backupConfigs.removeForReseller(tenantId),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  const verify = useMutation({
    mutationFn: (configId: string) => backupConfigs.verify(configId),
    onSuccess: (r) => setVerifyResult(r),
  });

  const config: BackupConfigView | null = data?.config ?? null;
  const featureDisabled = scope === 'customer' && (data as { featureDisabled?: boolean } | undefined)?.featureDisabled === true;
  const cardTitle = title ?? (scope === 'reseller' ? t('backupConfig.bucketTitleReseller') : t('backupConfig.bucketTitle'));

  function submit() {
    setSaveError(null);
    if (!accessKeyId.trim() || !secretAccessKey.trim()) {
      setSaveError(t('backupConfig.credentialsRequired'));
      return;
    }
    if (!endpoint.trim() || !bucket.trim() || !region.trim()) {
      setSaveError(t('backupConfig.fieldsRequired'));
      return;
    }
    save.mutate({
      enabled,
      provider,
      account_id: accountId.trim() || null,
      access_key_id: accessKeyId.trim(),
      secret_access_key: secretAccessKey.trim(),
      endpoint: endpoint.trim(),
      region: region.trim(),
      bucket: bucket.trim(),
      prefix: prefix.trim() || null,
    });
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <HardDriveDownload className="h-4 w-4 text-muted-foreground" />
              {cardTitle}
            </CardTitle>
            <CardDescription>
              {scope === 'customer' ? t('backupConfig.customerDescription') : t('backupConfig.resellerDescription')}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {featureDisabled ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <Lock className="h-8 w-8 text-muted-foreground/40" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Backup bucket not enabled</p>
              <p className="text-xs text-muted-foreground/70 mt-0.5">
                Contact your reseller to activate this feature for your account.
              </p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : !config || editing ? (
          <BackupForm
            initialConfig={config}
            provider={provider} setProvider={setProvider}
            accountId={accountId} setAccountId={setAccountId}
            accessKeyId={accessKeyId} setAccessKeyId={setAccessKeyId}
            secretAccessKey={secretAccessKey} setSecretAccessKey={setSecretAccessKey}
            endpoint={endpoint} setEndpoint={setEndpoint}
            region={region} setRegion={setRegion}
            bucket={bucket} setBucket={setBucket}
            prefix={prefix} setPrefix={setPrefix}
            enabled={enabled} setEnabled={setEnabled}
            saveError={saveError}
            onSave={submit}
            onCancel={config ? () => setEditing(false) : undefined}
            saving={save.isPending}
          />
        ) : (
          <BackupStatus
            config={config}
            onEdit={() => setEditing(true)}
            onVerify={() => { setVerifyResult(null); verify.mutate(config.id); }}
            onDelete={() => {
              if (confirm(t('backupConfig.removeConfirmMessage'))) {
                del.mutate();
              }
            }}
            verifying={verify.isPending}
            verifyResult={verifyResult}
            deleting={del.isPending}
          />
        )}
      </CardContent>
    </Card>
  );
}

function BackupForm(props: {
  initialConfig: BackupConfigView | null;
  provider: BackupProvider;
  setProvider: (p: BackupProvider) => void;
  accountId: string;
  setAccountId: (v: string) => void;
  accessKeyId: string;
  setAccessKeyId: (v: string) => void;
  secretAccessKey: string;
  setSecretAccessKey: (v: string) => void;
  endpoint: string;
  setEndpoint: (v: string) => void;
  region: string;
  setRegion: (v: string) => void;
  bucket: string;
  setBucket: (v: string) => void;
  prefix: string;
  setPrefix: (v: string) => void;
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  saveError: string | null;
  onSave: () => void;
  onCancel?: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const p = props;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        {(['r2', 's3'] as const).map((pv) => (
          <label
            key={pv}
            className={cn(
              'cursor-pointer rounded-md border-2 px-3 py-2 transition-all flex items-center gap-2',
              p.provider === pv
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-slate-400',
            )}
          >
            <input type="radio" className="sr-only"
              checked={p.provider === pv}
              onChange={() => p.setProvider(pv)}
            />
            <Cloud className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">
              {pv === 'r2' ? 'Cloudflare R2' : 'AWS S3'}
            </span>
          </label>
        ))}
      </div>

      {p.provider === 'r2' && (
        <div className="space-y-1">
          <Label>{t('backupConfig.accountId')}</Label>
          <Input
            value={p.accountId} onChange={(e) => p.setAccountId(e.target.value)}
            placeholder="abc123def456…"
            className="font-mono text-sm"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>{t('backupConfig.accessKeyId')}</Label>
          <Input
            value={p.accessKeyId}
            onChange={(e) => p.setAccessKeyId(e.target.value)}
            placeholder={p.initialConfig?.access_key_id_redacted ?? 'paste access key'}
            className="font-mono text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label>{t('backupConfig.secretAccessKey')}</Label>
          <Input
            type="password" value={p.secretAccessKey}
            onChange={(e) => p.setSecretAccessKey(e.target.value)}
            placeholder="••••••••"
            className="font-mono text-sm"
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label>{t('backupConfig.endpointUrl')}</Label>
        <Input
          value={p.endpoint} onChange={(e) => p.setEndpoint(e.target.value)}
          placeholder={
            p.provider === 'r2'
              ? 'https://<account>.r2.cloudflarestorage.com'
              : 'https://s3.<region>.amazonaws.com'
          }
          className="font-mono text-sm"
        />
      </div>

      <div className="grid grid-cols-[8rem_1fr_8rem] gap-3">
        <div className="space-y-1">
          <Label>{t('backupConfig.region')}</Label>
          <Input
            value={p.region} onChange={(e) => p.setRegion(e.target.value)}
            placeholder={p.provider === 'r2' ? 'auto' : 'us-east-1'}
            className="font-mono text-sm"
            disabled={p.provider === 'r2'}
          />
        </div>
        <div className="space-y-1">
          <Label>{t('backupConfig.bucket')}</Label>
          <Input
            value={p.bucket} onChange={(e) => p.setBucket(e.target.value)}
            placeholder="my-backup-bucket"
            className="font-mono text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label>{t('backupConfig.prefix')}</Label>
          <Input
            value={p.prefix} onChange={(e) => p.setPrefix(e.target.value)}
            placeholder={`(${t('common.optional')})`}
            className="font-mono text-sm"
          />
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
        <input type="checkbox" className="accent-primary h-4 w-4"
          checked={p.enabled}
          onChange={(e) => p.setEnabled(e.target.checked)}
        />
        <span>{t('backupConfig.enabled')}</span>
      </label>

      {p.saveError && <p role="alert" className="text-sm text-destructive">{p.saveError}</p>}

      <div className="flex items-center gap-2 pt-2">
        {p.onCancel && (
          <Button size="sm" variant="outline" onClick={p.onCancel} disabled={p.saving}>
            {t('common.cancel')}
          </Button>
        )}
        <Button size="sm" onClick={p.onSave} disabled={p.saving}>
          {p.saving ? t('common.saving') : p.initialConfig ? t('backupConfig.updateCredentials') : t('backupConfig.saveCredentials')}
        </Button>
      </div>
    </div>
  );
}

function BackupStatus(props: {
  config: BackupConfigView;
  onEdit: () => void;
  onVerify: () => void;
  onDelete: () => void;
  verifying: boolean;
  verifyResult: BackupVerifyResult | null;
  deleting: boolean;
}) {
  const { t } = useTranslation();
  const { config } = props;
  return (
    <div className="space-y-4">
      <div className={cn(
        'rounded-md border-l-4 p-3 bg-slate-50',
        config.enabled ? 'border-l-emerald-500' : 'border-l-slate-300',
      )}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm">
              <Cloud className="h-4 w-4 text-primary" />
              <span className="font-semibold">
                {config.provider === 'r2' ? 'Cloudflare R2' : 'AWS S3'}
              </span>
              <span className={cn(
                'text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded',
                config.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500',
              )}>
                {config.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
              <dt className="text-muted-foreground">Bucket</dt>
              <dd className="font-mono">{config.bucket}{config.prefix ? `/${config.prefix}` : ''}</dd>
              <dt className="text-muted-foreground">Endpoint</dt>
              <dd className="font-mono truncate">{config.endpoint}</dd>
              <dt className="text-muted-foreground">Region</dt>
              <dd className="font-mono">{config.region}</dd>
              <dt className="text-muted-foreground">Access key</dt>
              <dd className="font-mono">{config.access_key_id_redacted}</dd>
            </dl>
          </div>
        </div>
      </div>

      {config.last_sync_at && (
        <div className="text-xs text-muted-foreground">
          Last sync: {new Date(config.last_sync_at).toLocaleString()} —{' '}
          <span className={cn(
            config.last_sync_status === 'ok' ? 'text-emerald-700'
              : config.last_sync_status === 'error' ? 'text-rose-700'
                : 'text-muted-foreground',
          )}>
            {config.last_sync_status ?? 'unknown'}
          </span>
          {' · '}
          <span className="font-mono tabular-nums">
            {config.synced_object_count} objects · {(config.synced_bytes / 1024 / 1024).toFixed(1)} MB
          </span>
          {config.last_sync_error && (
            <p className="mt-1 text-rose-700 font-mono text-[11px] break-all">
              {config.last_sync_error}
            </p>
          )}
        </div>
      )}
      {!config.last_sync_at && (
        <p className="text-xs text-muted-foreground italic">
          {t('backupConfig.notSyncedYet')}
        </p>
      )}

      {props.verifyResult && (
        <div className={cn(
          'rounded-md border-l-4 p-2 text-xs',
          props.verifyResult.ok ? 'border-l-emerald-500 bg-emerald-50/40' : 'border-l-rose-500 bg-rose-50/40',
        )}>
          <div className="flex items-start gap-2">
            {props.verifyResult.ok ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
            )}
            <span className="font-mono text-[11px] break-all">
              {props.verifyResult.ok
                ? t('backupConfig.connectedTo', { bucket: props.verifyResult.bucket })
                : props.verifyResult.error}
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={props.onEdit}>
          {t('backupConfig.updateCredentials')}
        </Button>
        <Button
          size="sm" variant="outline"
          onClick={props.onVerify} disabled={props.verifying}
        >
          {props.verifying
            ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />{t('backupConfig.verifying')}</>
            : t('backupConfig.verifyConnection')}
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={props.onDelete} disabled={props.deleting}
          className="text-destructive hover:text-destructive hover:bg-destructive/5 ml-auto"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          {t('backupConfig.remove')}
        </Button>
      </div>
    </div>
  );
}
